import React, { useEffect, useState } from "react";
import dayjs from "dayjs";
import { toIsoDate } from "../../../utils/dayjs";
import { Input } from "../Input";
import { RentalDatePicker } from "./RentalDatePicker";
import { Dropdown } from "../Dropdown";
import "./rental-date-field.scss";

const rentalHeaderCopy = "Pick a delivery date 1-2 days before your event";
const toFormattedDate = (date: string | Date) => dayjs(new Date(date)).format("ddd, D/MM");

export const DEFAULT_RENTAL_PERIOD = 4;
export type RentalDatesType = {
  // string here is YYYY-MM-DD
  startDate: string;
  endDate: string;
};

type RentalDatesTypeState = RentalDatesType | undefined | null;

interface RentalDateFieldProps {
  availableDeliveryDates: string[];
  errorMessage?: string | undefined;
  initialAppliedDates?: { startDate: string | Date; endDate: string | Date };
  isDressAvailable: (date: Date) => boolean;
  onDatePicked?: (dates: RentalDatesType) => void;
  rentalPeriod?: number;
  shouldShowDatePicker?: boolean;
}

export const RentalDateField: React.FC<RentalDateFieldProps> = ({
  availableDeliveryDates,
  errorMessage,
  initialAppliedDates,
  isDressAvailable,
  onDatePicked,
  rentalPeriod,
  shouldShowDatePicker,
}) => {
  const [showingDatePicker, setShowingDatePicker] = useState<boolean>(false);
  const [dates, setDates] = useState<RentalDatesTypeState>(undefined);

  useEffect(() => {
    const propDates = initialAppliedDates;
    if (propDates) {
      setDates({
        startDate: toIsoDate(propDates.startDate),
        endDate: toIsoDate(propDates.endDate),
      });
    } else {
      setDates(undefined);
    }
  }, [initialAppliedDates]);

  const handleOnDatePicked = (dates: RentalDatesType) => {
    if (onDatePicked) {
      onDatePicked(dates);
    }

    setDates(dates);
  };

  let dateText = "";
  if (dates) {
    const { startDate, endDate } = dates;
    if (startDate !== "" && endDate !== "") {
      dateText = `${toFormattedDate(startDate)} - ${toFormattedDate(endDate)}`;
    }
  }

  return (
    <div className="c-rental-date-field">
      <Input
        className="c-rental-date-field__content"
        errorMessage={errorMessage}
        onClick={() => setShowingDatePicker(shouldShowDatePicker === false ? false : !showingDatePicker)}
        placeholder="Pick your delivery date"
        readOnly
        value={dateText}
        wrapperClass="c-rental-date-field__content_wrapper"
        aria-label="rental date field"
      />

      <Dropdown
        visible={shouldShowDatePicker === false ? false : showingDatePicker}
        onClickOutsideDropdown={() => setShowingDatePicker(false)}
        onlyRenderVisible
      >
        <RentalDatePicker
          header={rentalHeaderCopy}
          isDressAvailable={isDressAvailable}
          onDatePicked={handleOnDatePicked}
          highlightedDays={
            dates
              ? {
                  from: new Date(dates.startDate),
                  to: new Date(dates.endDate),
                }
              : undefined
          }
          rentalPeriod={rentalPeriod || DEFAULT_RENTAL_PERIOD}
          availableDeliveryDates={availableDeliveryDates}
          onClose={() => setShowingDatePicker(false)}
        />
      </Dropdown>
    </div>
  );
};
